import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './ChapterPlayer.module.css';
import SentencesSection from '../SentencesSection/SentencesSection';
import AudioControls from '../AudioControls/AudioControls';
import { parseSubtitleContent, buildSentenceTimingMap } from '../../../../utils/subtitle';

const PLAY_MODES = {
  EN_ONLY: 'en',
  CN_ONLY: 'cn',
  EN_CN: 'en_cn',
  CN_EN: 'cn_en'
};

const DUAL_MODE_ORDERS = {
  [PLAY_MODES.EN_CN]: ['en', 'cn'],
  [PLAY_MODES.CN_EN]: ['cn', 'en']
};

const PLAYBACK_SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];
const PRONOUNCE_END_TOLERANCE = 0.05;
const PRONOUNCE_FALLBACK_DURATION = 3;

const ensureAudioMetadata = (audio) => {
  if (!audio) return Promise.resolve();
  if (audio.readyState >= 1 && !Number.isNaN(audio.duration)) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const handleLoadedMetadata = () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      resolve();
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
    audio.load();
  });
};

const seekAudioTime = (audio, time) => {
  if (!audio) return Promise.resolve();
  const targetTime = Number.isFinite(time) ? Math.max(time, 0) : 0;

  if (Math.abs(audio.currentTime - targetTime) < 0.05) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const handleSeeked = () => {
      audio.removeEventListener('seeked', handleSeeked);
      resolve();
    };

    audio.addEventListener('seeked', handleSeeked, { once: true });

    try {
      audio.currentTime = targetTime;
    } catch (error) {
      audio.removeEventListener('seeked', handleSeeked);
      resolve();
    }
  });
};

const ChapterPlayer = ({
  chapter,
  audioUrls,
  displayMode,
  chineseTextColor,
  englishTextColor,
  fontSize = 16,
  lineHeight = 1.8,
  sentenceFavorites,
  sentenceFavoriteLoading,
  sentenceNoteLoading,
  highlightSentenceId,
  backgroundColor,
  chapterWordSet,
  onSentenceFavorite,
  onSentenceNote,
  onWordClick,
  onSentencePronounce,
  controlsContainer
}) => {
  const audioEnRef = useRef(null);
  const audioCnRef = useRef(null);
  const audioDualRef = useRef(null);
  const [playMode, setPlayMode] = useState(PLAY_MODES.EN_CN);
  const [isPlaying, setIsPlaying] = useState(false);
  const [subtitleCnSegments, setSubtitleCnSegments] = useState([]);
  const [subtitleEnSegments, setSubtitleEnSegments] = useState([]);
  const [dualModeSegments, setDualModeSegments] = useState([]);
  const dualModeActiveIndexRef = useRef(0);
  const [autoHighlightId, setAutoHighlightId] = useState(null);
  const lastScrolledIdRef = useRef(null);
  const transcriptContainerRef = useRef(null);
  const pronouncePlaybackRef = useRef({
    cancelCurrent: null,
    isCancelled: false,
    sentenceId: null,
  });
  const pendingResumeRef = useRef(null);
  const seekingRef = useRef(false);
  const wasPlayingBeforeSeekRef = useRef(false);

  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playbackSpeedRef = useRef(1);
  const [showPlaybackSettings, setShowPlaybackSettings] = useState(false);
  const playbackSettingsRef = useRef(null);
  
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const englishTimingMap = useMemo(
    () => buildSentenceTimingMap(subtitleEnSegments),
    [subtitleEnSegments]
  );
  const chineseTimingMap = useMemo(
    () => buildSentenceTimingMap(subtitleCnSegments),
    [subtitleCnSegments]
  );
  const sentenceTimings = useMemo(
    () => ({ en: englishTimingMap, cn: chineseTimingMap }),
    [englishTimingMap, chineseTimingMap]
  );

  const chapterId = chapter?.id;

  const audioEnSrc = useMemo(() => {
    if (audioUrls?.en_mp3) return audioUrls.en_mp3;
    if (!chapterId) return '/chapter1_en.mp3';
    return '';
  }, [chapterId, audioUrls]);

  const audioCnSrc = useMemo(() => {
    if (audioUrls?.cn_mp3) return audioUrls.cn_mp3;
    if (!chapterId) return '/chapter1_cn.mp3';
    return '';
  }, [chapterId, audioUrls]);

  const audioDualSrc = useMemo(() => {
    if (audioUrls) {
      if (playMode === PLAY_MODES.CN_EN) return audioUrls.cn_en_mp3 || '';
      if (playMode === PLAY_MODES.EN_CN) return audioUrls.en_cn_mp3 || '';
      return '';
    }
    if (!chapterId) {
      if (playMode === PLAY_MODES.CN_EN) return '/chapter1_cn_en.mp3';
      if (playMode === PLAY_MODES.EN_CN) return '/chapter1_en_cn.mp3';
      return '';
    }
    return '';
  }, [chapterId, playMode, audioUrls]);

  const srtEnSrc = useMemo(() => {
    if (audioUrls?.en_srt) return audioUrls.en_srt;
    if (!chapterId) return '/chapter1_en.srt';
    return '';
  }, [chapterId, audioUrls]);

  const srtCnSrc = useMemo(() => {
    if (audioUrls?.cn_srt) return audioUrls.cn_srt;
    if (!chapterId) return '/chapter1_cn.srt';
    return '';
  }, [chapterId, audioUrls]);

  const srtDualSrc = useMemo(() => {
    if (audioUrls) {
      if (playMode === PLAY_MODES.CN_EN) return audioUrls.cn_en_srt || '';
      if (playMode === PLAY_MODES.EN_CN) return audioUrls.en_cn_srt || '';
      return '';
    }
    if (!chapterId) {
      if (playMode === PLAY_MODES.CN_EN) return '/chapter1_cn_en.srt';
      if (playMode === PLAY_MODES.EN_CN) return '/chapter1_en_cn.srt';
      return '';
    }
    return '';
  }, [chapterId, playMode, audioUrls]);

  const formatTime = useCallback((seconds) => {
    if (!Number.isFinite(seconds)) {
      return '00:00';
    }
    const clamped = Math.max(0, Math.floor(seconds));
    const mins = String(Math.floor(clamped / 60)).padStart(2, '0');
    const secs = String(clamped % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  }, []);

  const parseSubtitles = useCallback((content) => parseSubtitleContent(content), []);

  const buildDualModeSegments = useCallback((segments, mode) => {
    if (!Array.isArray(segments) || segments.length === 0) {
      return [];
    }

    const order = DUAL_MODE_ORDERS[mode] || [];
    const fallbackOrder = order.length ? order : ['en', 'cn'];
    const sentenceState = new Map();
    let fallbackCounter = 0;

    return segments.map((segment) => {
      const hasSentenceId = segment.sentenceId !== null && segment.sentenceId !== undefined;
      const key = hasSentenceId ? segment.sentenceId : `__fallback_${fallbackCounter++}`;
      const previous = sentenceState.get(key);
      const pairIndex = previous ? previous.pairIndex : sentenceState.size;
      const orderIndex = previous ? previous.orderIndex + 1 : 0;
      const language =
        fallbackOrder[orderIndex % fallbackOrder.length] || fallbackOrder[0] || 'en';

      sentenceState.set(key, { pairIndex, orderIndex });

      return {
        ...segment,
        language,
        pairIndex,
        orderIndex,
        sentenceId: hasSentenceId ? segment.sentenceId : null,
      };
    });
  }, []);

  useEffect(() => {
    if (!showPlaybackSettings) return undefined;
    const handleClickOutside = (e) => {
      if (playbackSettingsRef.current && !playbackSettingsRef.current.contains(e.target)) {
        setShowPlaybackSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPlaybackSettings]);

  const getActiveSubtitleSegments = useCallback(() => {
    if (playMode === PLAY_MODES.EN_ONLY) {
      return subtitleEnSegments;
    }
    if (playMode === PLAY_MODES.CN_ONLY) {
      return subtitleCnSegments;
    }
    return dualModeSegments;
  }, [playMode, subtitleCnSegments, subtitleEnSegments, dualModeSegments]);

  const getPrimaryAudio = useCallback(() => {
    switch (playMode) {
      case PLAY_MODES.CN_ONLY:
        return audioCnRef.current;
      case PLAY_MODES.EN_CN:
      case PLAY_MODES.CN_EN:
        return audioDualRef.current;
      case PLAY_MODES.EN_ONLY:
      default:
        return audioEnRef.current;
    }
  }, [playMode]);

  const pauseAllAudios = useCallback(() => {
    [audioEnRef.current, audioCnRef.current, audioDualRef.current].forEach((audio) => {
      if (audio) {
        audio.pause();
      }
    });
  }, []);

  const stopPronouncePlayback = useCallback(() => {
    const playback = pronouncePlaybackRef.current;
    playback.isCancelled = true;
    if (typeof playback.cancelCurrent === 'function') {
      playback.cancelCurrent();
    }
    playback.cancelCurrent = null;
    playback.sentenceId = null;
  }, []);

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;

    const applyPlaybackRate = (audioElement) => {
      if (audioElement) {
        audioElement.playbackRate = playbackSpeed;
        audioElement.defaultPlaybackRate = playbackSpeed;
      }
    };

    applyPlaybackRate(audioEnRef.current);
    applyPlaybackRate(audioCnRef.current);
    applyPlaybackRate(audioDualRef.current);
  }, [playbackSpeed]);

  useEffect(
    () => () => {
      stopPronouncePlayback();
    },
    [stopPronouncePlayback]
  );

  useEffect(() => {
    stopPronouncePlayback();
  }, [chapterId, stopPronouncePlayback]);

  const playPronounceSegment = useCallback(
    async (audio, start = 0, end = null) => {
      if (!audio) return;
      const playback = pronouncePlaybackRef.current;

      await ensureAudioMetadata(audio);
      if (playback.isCancelled) return;

      await seekAudioTime(audio, start ?? 0);
      if (playback.isCancelled) return;

      await new Promise((resolve) => {
        let finished = false;

        const finish = () => {
          if (finished) return;
          finished = true;
          audio.pause();
          audio.removeEventListener('timeupdate', handleTimeUpdate);
          audio.removeEventListener('ended', handleEnded);
          if (playback.cancelCurrent === finish) {
            playback.cancelCurrent = null;
          }
          resolve();
        };

        const handleTimeUpdate = () => {
          if (playback.isCancelled) {
            finish();
            return;
          }
          if (end != null && audio.currentTime >= end - PRONOUNCE_END_TOLERANCE) {
            finish();
          }
        };

        const handleEnded = () => {
          finish();
        };

        playback.cancelCurrent = () => {
          playback.isCancelled = true;
          finish();
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.playbackRate = playbackSpeed;

        const playPromise = audio.play();
        if (playPromise?.catch) {
          playPromise.catch(() => finish());
        }
      });
    },
    [playbackSpeed]
  );

  const runInternalSentencePronounce = useCallback(
    async (sentenceId, payload = {}) => {
      const playback = pronouncePlaybackRef.current;

      if (playback.sentenceId === sentenceId && !playback.isCancelled) {
        stopPronouncePlayback();
        return;
      }

      const timings = payload?.timings ?? {};
      const segments = [];

      if (timings.en?.start != null) {
        segments.push({ language: 'en', start: timings.en.start, end: timings.en.end, audio: audioEnRef.current });
      }
      if (timings.cn?.start != null) {
        segments.push({ language: 'cn', start: timings.cn.start, end: timings.cn.end, audio: audioCnRef.current });
      }

      if (!segments.length) {
        console.warn('句子缺少可播放的时间戳', sentenceId, timings);
        return;
      }

      stopPronouncePlayback();
      pauseAllAudios();
      setIsPlaying(false);
      playback.isCancelled = false;
      playback.sentenceId = sentenceId !== undefined && sentenceId !== null ? sentenceId : null;
      if (sentenceId !== undefined && sentenceId !== null) {
        setAutoHighlightId((prev) => (prev === sentenceId ? prev : sentenceId));
      }

      for (const segment of segments) {
        if (playback.isCancelled) break;
        const audio = segment.audio;
        if (!audio) {
          continue;
        }

        const startValue = Number.isFinite(segment.start) ? Math.max(segment.start, 0) : 0;
        const endValue = Number.isFinite(segment.end)
          ? Math.max(segment.end, startValue)
          : startValue + PRONOUNCE_FALLBACK_DURATION;

        try {
          await playPronounceSegment(audio, startValue, endValue);
        } catch (error) {
          console.error('播放句子音频失败:', error);
          playback.isCancelled = true;
        }
      }

      if (playback.sentenceId === sentenceId) {
        playback.sentenceId = null;
        playback.cancelCurrent = null;
        playback.isCancelled = false;
      }
    },
    [pauseAllAudios, playPronounceSegment, stopPronouncePlayback]
  );

  const handleSentencePronounce = useCallback(
    (sentenceId, payload) => {
      if (typeof onSentencePronounce === 'function') {
        onSentencePronounce(sentenceId, payload);
        return;
      }
      runInternalSentencePronounce(sentenceId, payload);
    },
    [onSentencePronounce, runInternalSentencePronounce]
  );

  const getCurrentAudio = useCallback(() => getPrimaryAudio(), [getPrimaryAudio]);

  const findActiveSegment = useCallback((segments, time) => {
    if (!segments.length) {
      return null;
    }

    // 先尝试精确匹配当前时间所在的段落
    let index = segments.findIndex((segment) => {
      if (segment.end && segment.end > segment.start) {
        return time >= segment.start && time < segment.end;
      }
      return time >= segment.start;
    });

    if (index === -1) {
      // 如果找不到精确匹配,说明在段落间隙或边界外
      if (time < segments[0].start) {
        // 时间在第一个段落之前,返回第一个段落
        index = 0;
      } else {
        // 时间在段落之后(可能在段落间隙或最后),找到之前最近的段落
        // 从后往前找,找到第一个 start <= time 的段落
        for (let i = segments.length - 1; i >= 0; i--) {
          if (time >= segments[i].start) {
            index = i;
            break;
          }
        }
        // 如果还是找不到,返回第一个段落(理论上不会发生)
        if (index === -1) {
          index = 0;
        }
      }
    }

    return segments[index] || null;
  }, []);

  useEffect(() => {
    const audioElements = [
      audioEnRef.current,
      audioCnRef.current,
      audioDualRef.current,
    ].filter(Boolean);
    if (audioElements.length === 0) {
      return undefined;
    }

    const handleTimeUpdate = (event) => {
      const audio = event.currentTarget;
      if (audio !== getCurrentAudio()) {
        return;
      }

      const segments = getActiveSubtitleSegments();
      if (!segments.length) {
        return;
      }

      const currentTimeValue = audio.currentTime ?? 0;
      const activeSegment = findActiveSegment(segments, currentTimeValue);
      if (!activeSegment) {
        return;
      }

      if (playMode === PLAY_MODES.EN_CN || playMode === PLAY_MODES.CN_EN) {
        const segmentIndex = segments.indexOf(activeSegment);
        if (segmentIndex !== -1) {
          dualModeActiveIndexRef.current = segmentIndex;
        }
      }

      const highlightId = activeSegment.sentenceId ?? activeSegment.id ?? null;
      if (highlightId !== null) {
        setAutoHighlightId((prev) => (prev === highlightId ? prev : highlightId));
      }
    };

    audioElements.forEach((audio) => {
      audio.addEventListener('timeupdate', handleTimeUpdate);
    });

    return () => {
      audioElements.forEach((audio) => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
      });
    };
  }, [
    findActiveSegment,
    getActiveSubtitleSegments,
    getCurrentAudio,
    playMode,
    dualModeSegments,
  ]);

  const seekCurrentSegment = useCallback(() => {
    const audio = getCurrentAudio();
    if (!audio) {
      return;
    }

    if (playMode === PLAY_MODES.EN_CN || playMode === PLAY_MODES.CN_EN) {
      const activeSegment = dualModeSegments[dualModeActiveIndexRef.current];
      if (activeSegment) {
        pauseAllAudios();
        audio.currentTime = activeSegment.start ?? 0;
        dualModeActiveIndexRef.current = Math.max(
          0,
          dualModeSegments.indexOf(activeSegment)
        );
      }
    }
  }, [getCurrentAudio, playMode, dualModeSegments, pauseAllAudios]);

  useEffect(() => {
    let cancelled = false;

    const loadSubtitle = async (src, setter) => {
      if (!src) {
        setter([]);
        return;
      }

      try {
        const response = await fetch(src, { cache: 'no-cache' });
        if (!response.ok) {
          throw new Error(`获取字幕失败(${response.status})`);
        }
        const text = await response.text();
        if (!cancelled) {
          setter(parseSubtitles(text));
        }
      } catch (error) {
        console.error('加载字幕失败:', src, error);
        if (!cancelled) {
          setter([]);
        }
      }
    };

    loadSubtitle(srtCnSrc, setSubtitleCnSegments);
    loadSubtitle(srtEnSrc, setSubtitleEnSegments);

    return () => {
      cancelled = true;
    };
  }, [parseSubtitles, srtCnSrc, srtEnSrc]);
  useEffect(() => {
    let cancelled = false;

    if (playMode !== PLAY_MODES.EN_CN && playMode !== PLAY_MODES.CN_EN) {
      dualModeActiveIndexRef.current = 0;
      setDualModeSegments([]);
      return () => {
        cancelled = true;
      };
    }

    if (!srtDualSrc) {
      dualModeActiveIndexRef.current = 0;
      setDualModeSegments([]);
      return () => {
        cancelled = true;
      };
    }

    setDualModeSegments([]);
    const loadDualSubtitle = async () => {
      try {
        const response = await fetch(srtDualSrc, { cache: 'no-cache' });
        if (!response.ok) {
          throw new Error(`获取双语字幕失败(${response.status})`);
        }
        const text = await response.text();
        if (!cancelled) {
          const parsed = parseSubtitles(text);
          dualModeActiveIndexRef.current = 0;
          setDualModeSegments(buildDualModeSegments(parsed, playMode));
        }
      } catch (error) {
        console.error('加载双语字幕失败:', srtDualSrc, error);
        if (!cancelled) {
          dualModeActiveIndexRef.current = 0;
          setDualModeSegments([]);
        }
      }
    };

    loadDualSubtitle();

    return () => {
      cancelled = true;
    };
  }, [playMode, srtDualSrc, parseSubtitles, buildDualModeSegments]);

  useEffect(() => {
    const resume = pendingResumeRef.current;
    const primaryAudio = getPrimaryAudio();

    if (primaryAudio && primaryAudio.duration) {
      setDuration(primaryAudio.duration);
    }

    if (!resume) {
      dualModeActiveIndexRef.current = 0;
      seekCurrentSegment();
      return;
    }

    const isDual = playMode === PLAY_MODES.EN_CN || playMode === PLAY_MODES.CN_EN;

    if (isDual && dualModeSegments.length === 0) {
      return;
    }

    const targetSentenceId = resume.sentenceId;

    let segments;
    let audio;
    if (isDual) {
      segments = dualModeSegments;
      audio = audioDualRef.current;
    } else if (playMode === PLAY_MODES.CN_ONLY) {
      segments = subtitleCnSegments;
      audio = audioCnRef.current;
    } else {
      segments = subtitleEnSegments;
      audio = audioEnRef.current;
    }

    if (!audio || !segments.length) return;

    pendingResumeRef.current = null;

    let targetIndex = targetSentenceId != null
      ? segments.findIndex((s) => s.sentenceId === targetSentenceId)
      : -1;
    if (targetIndex === -1) targetIndex = 0;

    const seg = segments[targetIndex];
    if (isDual) dualModeActiveIndexRef.current = targetIndex;

    const seekTime = seg.start ?? 0;
    pauseAllAudios();
    audio.playbackRate = playbackSpeedRef.current;
    if (seg.sentenceId != null) {
      setAutoHighlightId(seg.sentenceId);
    }

    const doSeekAndPlay = () => {
      audio.currentTime = seekTime;
      setCurrentTime(seekTime);
      if (resume.wasPlaying) {
        const p = audio.play();
        if (p?.then) p.then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        else setIsPlaying(true);
      }
    };

    if (isDual && (audio.readyState < 1 || !Number.isFinite(audio.duration))) {
      const onCanSeek = () => {
        audio.removeEventListener('loadedmetadata', onCanSeek);
        doSeekAndPlay();
      };
      audio.addEventListener('loadedmetadata', onCanSeek);
    } else {
      doSeekAndPlay();
    }
  }, [playMode, dualModeSegments, subtitleCnSegments, subtitleEnSegments, seekCurrentSegment, getPrimaryAudio, pauseAllAudios]);

  useEffect(() => {
    setAutoHighlightId(null);
    lastScrolledIdRef.current = null;
    setCurrentTime(0);
  }, [chapterId]);


  useEffect(() => {
    const audioElements = [
      audioEnRef.current,
      audioCnRef.current,
      audioDualRef.current,
    ].filter(Boolean);
    if (audioElements.length === 0) {
      return undefined;
    }

    const handlePlayEvent = () => {
      setIsPlaying(true);
    };

    const handlePauseEvent = () => {
      setIsPlaying(false);
    };

    const handleEndedEvent = () => {
      setIsPlaying(false);
      setAutoHighlightId(null);
    };

    const handleBufferingEvent = (event) => {
      const audio = event.currentTarget;
      console.info(`音频缓冲: ${event.type}`, {
        currentTime: audio?.currentTime,
        readyState: audio?.readyState,
        networkState: audio?.networkState,
      });
    };

    const handleErrorEvent = (event) => {
      const audio = event.currentTarget;
      const mediaError = audio?.error;
      console.error('音频错误:', {
        type: event.type,
        code: mediaError?.code,
        message: mediaError?.message,
        networkState: audio?.networkState,
        readyState: audio?.readyState,
        currentTime: audio?.currentTime,
        src: audio?.currentSrc || audio?.src,
      });
      setIsPlaying(false);
    };

    audioElements.forEach((audio) => {
      audio.addEventListener('play', handlePlayEvent);
      audio.addEventListener('playing', handlePlayEvent);
      audio.addEventListener('pause', handlePauseEvent);
      audio.addEventListener('ended', handleEndedEvent);
      audio.addEventListener('waiting', handleBufferingEvent);
      audio.addEventListener('stalled', handleBufferingEvent);
      audio.addEventListener('suspend', handleBufferingEvent);
      audio.addEventListener('error', handleErrorEvent);
    });

    return () => {
      audioElements.forEach((audio) => {
        audio.removeEventListener('play', handlePlayEvent);
        audio.removeEventListener('playing', handlePlayEvent);
        audio.removeEventListener('pause', handlePauseEvent);
        audio.removeEventListener('ended', handleEndedEvent);
        audio.removeEventListener('waiting', handleBufferingEvent);
        audio.removeEventListener('stalled', handleBufferingEvent);
        audio.removeEventListener('suspend', handleBufferingEvent);
        audio.removeEventListener('error', handleErrorEvent);
      });
    };
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      lastScrolledIdRef.current = null;
    }
  }, [isPlaying]);

  // 监听音频时间和时长更新
  useEffect(() => {
    const audioElements = [
      audioEnRef.current,
      audioCnRef.current,
      audioDualRef.current,
    ].filter(Boolean);
    if (audioElements.length === 0) {
      return undefined;
    }

    const handleLoadedMetadata = (event) => {
      const audio = event.currentTarget;
      // 获取当前主音频的时长
      const primaryAudio = getPrimaryAudio();
      if (audio === primaryAudio) {
        setDuration(audio.duration || 0);
      }
    };

    const handleProgressTimeUpdate = (event) => {
      if (seekingRef.current) return;
      const audio = event.currentTarget;
      const currentAudio = getCurrentAudio();
      if (audio === currentAudio) {
        setCurrentTime(audio.currentTime || 0);
      }
    };

    audioElements.forEach((audio) => {
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleProgressTimeUpdate);
    });

    return () => {
      audioElements.forEach((audio) => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleProgressTimeUpdate);
      });
    };
  }, [getCurrentAudio, getPrimaryAudio]);

  // 自动滚动到高亮段落
  useEffect(() => {
    // 如果没有高亮ID,不滚动
    if (!autoHighlightId) {
      return;
    }

    // 如果已经滚动到这个段落,不重复滚动
    if (lastScrolledIdRef.current === autoHighlightId) {
      return;
    }

    // 获取句子元素
    const element = document.getElementById(`sentence-${autoHighlightId}`);
    if (!element) {
      console.warn(`找不到句子元素: sentence-${autoHighlightId}`);
      return;
    }

    // 获取 navbar 高度
    const navbar = document.querySelector('nav') || document.querySelector('[role="navigation"]');
    const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0;
    
    // 获取底部控制条高度
    const controlPanel = controlsContainer || document.querySelector('[class*="controlPanel"]');
    const controlHeight = controlPanel ? controlPanel.getBoundingClientRect().height : 0;
    
    // 计算可视区域高度 (屏幕高度 - navbar - control)
    const viewportHeight = window.innerHeight - navbarHeight - controlHeight;
    
    // 获取段落顶部的 Y 坐标 (相对于 viewport)
    const elementRect = element.getBoundingClientRect();
    const lineTop = elementRect.top;
    
    // navbar 底边的 Y 坐标
    const containerTop = navbarHeight;
    
    // 可视区域中心位置
    const centerPosition = containerTop + viewportHeight / 2;
    
    // 如果段落距离可视区域中心超过 40px,就滚动
    const distance = Math.abs(lineTop - centerPosition);
    
    console.log('滚动检查:', {
      autoHighlightId,
      lineTop,
      centerPosition,
      distance,
      navbarHeight,
      controlHeight,
      viewportHeight,
      shouldScroll: distance > 40
    });
    
    if (distance > 40) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
      
      lastScrolledIdRef.current = autoHighlightId;
    } else {
      // 距离中心不远,不需要滚动,但标记为已处理
      lastScrolledIdRef.current = autoHighlightId;
    }
  }, [autoHighlightId, controlsContainer]);

  // 开始播放（暂停后恢复时不 seek，直接 play）
  const handlePlay = useCallback(() => {
    stopPronouncePlayback();
    let targetAudio = getCurrentAudio();

    if (playMode === PLAY_MODES.EN_CN || playMode === PLAY_MODES.CN_EN) {
      if (!dualModeSegments.length) {
        console.warn('双语模式缺少字幕片段，回退至主音轨播放');
      } else {
        targetAudio = audioDualRef.current;
        if (!targetAudio) {
          console.error('❌ 双语模式音频元素不存在');
          return;
        }
      }
    }

    if (!targetAudio) {
      console.error('❌ 音频元素不存在');
      return;
    }

    targetAudio.playbackRate = playbackSpeed;
    const playPromise = targetAudio.play();

    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.error('播放失败:', err);
          setIsPlaying(false);
        });
    } else {
      setIsPlaying(true);
    }
  }, [getCurrentAudio, playbackSpeed, playMode, dualModeSegments, stopPronouncePlayback]);

  // 暂停播放
  const handlePause = useCallback(() => {
    stopPronouncePlayback();
    pauseAllAudios();
    setIsPlaying(false);
  }, [pauseAllAudios, stopPronouncePlayback]);

  // 停止播放
  const handleStop = useCallback(() => {
    stopPronouncePlayback();
    pauseAllAudios();
    [audioEnRef.current, audioCnRef.current, audioDualRef.current].forEach((audio) => {
      if (audio) {
        audio.currentTime = 0;
      }
    });
    dualModeActiveIndexRef.current = 0;
    setIsPlaying(false);
    setAutoHighlightId(null);
    setCurrentTime(0);
  }, [pauseAllAudios, stopPronouncePlayback]);

  const updateHighlightForTime = useCallback((value) => {
    if (playMode === PLAY_MODES.EN_CN || playMode === PLAY_MODES.CN_EN) {
      if (!dualModeSegments.length) return;
      let targetIndex = -1;
      for (let i = 0; i < dualModeSegments.length; i++) {
        const segment = dualModeSegments[i];
        if (value >= segment.start && (segment.end ? value < segment.end : true)) {
          targetIndex = i;
          break;
        }
      }
      if (targetIndex === -1) {
        if (value < dualModeSegments[0].start) {
          targetIndex = 0;
        } else {
          for (let i = dualModeSegments.length - 1; i >= 0; i--) {
            if (value >= dualModeSegments[i].start) { targetIndex = i; break; }
          }
        }
      }
      if (targetIndex !== -1) {
        dualModeActiveIndexRef.current = targetIndex;
        const segment = dualModeSegments[targetIndex];
        const highlightId = segment.sentenceId ?? segment.id ?? null;
        if (highlightId !== null) setAutoHighlightId(highlightId);
      }
    } else {
      const segments = getActiveSubtitleSegments();
      if (segments.length) {
        const segment = findActiveSegment(segments, value);
        if (segment) {
          const highlightId = segment.sentenceId ?? segment.id ?? null;
          if (highlightId !== null) setAutoHighlightId(highlightId);
        }
      }
    }
  }, [playMode, dualModeSegments, getActiveSubtitleSegments, findActiveSegment]);

  // 拖动中：只更新 UI，不 seek 音频
  const handleProgressDragging = useCallback((event) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value)) return;

    if (!seekingRef.current) {
      seekingRef.current = true;
      const audio = getCurrentAudio();
      wasPlayingBeforeSeekRef.current = audio ? !audio.paused : false;
      if (audio && !audio.paused) audio.pause();
    }

    setCurrentTime(value);
    updateHighlightForTime(value);
  }, [getCurrentAudio, updateHighlightForTime]);

  // 松手后：执行一次 seek 并恢复播放
  const handleProgressCommit = useCallback((event) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value)) return;

    const audio = getCurrentAudio();
    if (audio) {
      audio.currentTime = value;
      if (wasPlayingBeforeSeekRef.current) {
        audio.playbackRate = playbackSpeed;
        const p = audio.play();
        if (p?.then) p.then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      }
    }
    seekingRef.current = false;
    wasPlayingBeforeSeekRef.current = false;
  }, [getCurrentAudio, playbackSpeed]);

  // 点击句子行：跳转到该句时间戳，并同步底部进度条与高亮
  const handleSentenceClick = useCallback(
    (sentenceId, payload = {}) => {
      const audio = getCurrentAudio();
      if (!audio) return;

      let seekTime = null;
      const fallbackStart = payload.startTime != null ? Number(payload.startTime) : null;

      if (playMode === PLAY_MODES.EN_CN || playMode === PLAY_MODES.CN_EN) {
        if (dualModeSegments.length === 0) return;
        const idx = dualModeSegments.findIndex(
          (seg) => seg.sentenceId === sentenceId || seg.sentenceId === Number(sentenceId)
        );
        if (idx === -1) return;
        const seg = dualModeSegments[idx];
        seekTime = seg.start ?? fallbackStart;
        dualModeActiveIndexRef.current = idx;
      } else if (playMode === PLAY_MODES.EN_ONLY) {
        const timing = sentenceTimings?.en?.[sentenceId] ?? sentenceTimings?.en?.[String(sentenceId)];
        seekTime = timing?.start ?? fallbackStart;
      } else if (playMode === PLAY_MODES.CN_ONLY) {
        const timing = sentenceTimings?.cn?.[sentenceId] ?? sentenceTimings?.cn?.[String(sentenceId)];
        seekTime = timing?.start ?? fallbackStart;
      }

      if (seekTime == null || !Number.isFinite(seekTime)) return;

      stopPronouncePlayback();
      pauseAllAudios();
      audio.currentTime = seekTime;
      setCurrentTime(seekTime);
      setAutoHighlightId(sentenceId);
      audio.playbackRate = playbackSpeed;
      const playPromise = audio.play();
      if (playPromise?.then) {
        playPromise.then(() => setIsPlaying(true)).catch((err) => {
          console.error('跳转后播放失败:', err);
          setIsPlaying(false);
        });
      } else {
        setIsPlaying(true);
      }
    },
    [
      getCurrentAudio,
      playMode,
      dualModeSegments,
      sentenceTimings,
      pauseAllAudios,
      stopPronouncePlayback,
      playbackSpeed,
    ]
  );

  // 上一句
  const handlePrevSegment = useCallback(() => {
    if (playMode === PLAY_MODES.EN_CN || playMode === PLAY_MODES.CN_EN) {
      // 双语模式
      if (!dualModeSegments.length) return;

      const currentIndex = dualModeActiveIndexRef.current;
      const prevIndex = Math.max(0, currentIndex - 1);
      
      if (prevIndex === currentIndex && currentIndex === 0) return;

      const prevSegment = dualModeSegments[prevIndex];
      if (!prevSegment) return;

      const targetAudio = audioDualRef.current;
      if (!targetAudio) return;

      pauseAllAudios();
      dualModeActiveIndexRef.current = prevIndex;
      targetAudio.currentTime = prevSegment.start ?? 0;
      targetAudio.playbackRate = playbackSpeed;
      
      const highlightId = prevSegment.sentenceId ?? prevSegment.id ?? null;
      if (highlightId !== null) {
        setAutoHighlightId(highlightId);
      }

      const playPromise = targetAudio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch((err) => {
          console.error('播放失败:', err);
          setIsPlaying(false);
        });
      }
    } else {
      // 单语模式
      const audio = getCurrentAudio();
      if (!audio) return;

      const segments = getActiveSubtitleSegments();
      if (!segments.length) return;

      const currentTime = audio.currentTime ?? 0;
      const currentSegment = findActiveSegment(segments, currentTime);
      
      if (!currentSegment) return;

      const currentIndex = segments.indexOf(currentSegment);
      const prevIndex = Math.max(0, currentIndex - 1);
      
      if (prevIndex === currentIndex && currentIndex === 0) return;

      const prevSegment = segments[prevIndex];
      if (!prevSegment) return;

      audio.currentTime = prevSegment.start ?? 0;
      
      const highlightId = prevSegment.sentenceId ?? prevSegment.id ?? null;
      if (highlightId !== null) {
        setAutoHighlightId(highlightId);
      }

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch((err) => {
          console.error('播放失败:', err);
          setIsPlaying(false);
        });
      }
    }
  }, [playMode, dualModeSegments, getCurrentAudio, getActiveSubtitleSegments, findActiveSegment, pauseAllAudios, playbackSpeed]);

  // 下一句
  const handleNextSegment = useCallback(() => {
    if (playMode === PLAY_MODES.EN_CN || playMode === PLAY_MODES.CN_EN) {
      // 双语模式
      if (!dualModeSegments.length) return;

      const currentIndex = dualModeActiveIndexRef.current;
      const nextIndex = Math.min(dualModeSegments.length - 1, currentIndex + 1);
      
      if (nextIndex === currentIndex && currentIndex === dualModeSegments.length - 1) return;

      const nextSegment = dualModeSegments[nextIndex];
      if (!nextSegment) return;

      const targetAudio = audioDualRef.current;
      if (!targetAudio) return;

      pauseAllAudios();
      dualModeActiveIndexRef.current = nextIndex;
      targetAudio.currentTime = nextSegment.start ?? 0;
      targetAudio.playbackRate = playbackSpeed;
      
      const highlightId = nextSegment.sentenceId ?? nextSegment.id ?? null;
      if (highlightId !== null) {
        setAutoHighlightId(highlightId);
      }

      const playPromise = targetAudio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch((err) => {
          console.error('播放失败:', err);
          setIsPlaying(false);
        });
      }
    } else {
      // 单语模式
      const audio = getCurrentAudio();
      if (!audio) return;

      const segments = getActiveSubtitleSegments();
      if (!segments.length) return;

      const currentTime = audio.currentTime ?? 0;
      const currentSegment = findActiveSegment(segments, currentTime);
      
      if (!currentSegment) return;

      const currentIndex = segments.indexOf(currentSegment);
      const nextIndex = Math.min(segments.length - 1, currentIndex + 1);
      
      if (nextIndex === currentIndex && currentIndex === segments.length - 1) return;

      const nextSegment = segments[nextIndex];
      if (!nextSegment) return;

      audio.currentTime = nextSegment.start ?? 0;
      
      const highlightId = nextSegment.sentenceId ?? nextSegment.id ?? null;
      if (highlightId !== null) {
        setAutoHighlightId(highlightId);
      }

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch((err) => {
          console.error('播放失败:', err);
          setIsPlaying(false);
        });
      }
    }
  }, [playMode, dualModeSegments, getCurrentAudio, getActiveSubtitleSegments, findActiveSegment, pauseAllAudios, playbackSpeed]);

  const hasContent = useMemo(
    () => Boolean(chapter?.content || chapter?.content_trans),
    [chapter]
  );

  const PLAY_MODE_OPTIONS = useMemo(() => [
    { value: PLAY_MODES.CN_ONLY, label: '仅中文' },
    { value: PLAY_MODES.EN_ONLY, label: '仅英文' },
    { value: PLAY_MODES.EN_CN, label: '英 → 中' },
    { value: PLAY_MODES.CN_EN, label: '中 → 英' }
  ], []);

  const currentModeLabel = useMemo(
    () => PLAY_MODE_OPTIONS.find((m) => m.value === playMode)?.label ?? '英 → 中',
    [playMode, PLAY_MODE_OPTIONS]
  );

  // 播放模式设置按钮 (作为 leftControls)
  const playbackModeSettings = (
    <div className={styles.playbackSettingsContainer} ref={playbackSettingsRef}>
      <button
        type="button"
        className={styles.playbackSettingsBtn}
        onClick={() => setShowPlaybackSettings((prev) => !prev)}
        aria-label="播放顺序"
        aria-haspopup="true"
        aria-expanded={showPlaybackSettings}
      >
        <span className="material-symbols-rounded" style={{ fontSize: 18 }}>swap_horiz</span>
        <span className={styles.playbackSettingsBtnLabel}>{currentModeLabel}</span>
      </button>

      {showPlaybackSettings && (
        <div className={styles.playbackSettingsPanel} role="menu" aria-label="播放顺序">
          <p className={styles.playbackSettingsLabel}>播放顺序</p>
          {PLAY_MODE_OPTIONS.map((mode) => {
            const active = playMode === mode.value;
            return (
              <button
                key={mode.value}
                type="button"
                className={`${styles.modeOptionBtn} ${active ? styles.modeOptionActive : ''}`}
                onClick={() => {
                  if (active) return;
                  if (isPlaying) {
                    pendingResumeRef.current = {
                      sentenceId: autoHighlightId,
                      wasPlaying: true,
                    };
                    pauseAllAudios();
                  }
                  if (mode.value === PLAY_MODES.EN_CN || mode.value === PLAY_MODES.CN_EN) {
                    setDualModeSegments([]);
                  }
                  setPlayMode(mode.value);
                  setShowPlaybackSettings(false);
                }}
                role="menuitemradio"
                aria-checked={active}
              >
                {mode.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const controlsWrapper = (
    <AudioControls
      currentTime={currentTime}
      duration={duration}
      onProgressChange={handleProgressDragging}
      onProgressCommit={handleProgressCommit}
      formatTime={formatTime}
      isPlaying={isPlaying}
      onPlayPause={isPlaying ? handlePause : handlePlay}
      onPrevSegment={handlePrevSegment}
      onNextSegment={handleNextSegment}
      disableTransport={!hasContent}
      playbackSpeed={playbackSpeed}
      speedOptions={PLAYBACK_SPEEDS}
      onSpeedChange={setPlaybackSpeed}
      leftControls={playbackModeSettings}
    />
  );

  const controlsHost = controlsContainer;
  const effectiveHighlightId =
    highlightSentenceId !== null && highlightSentenceId !== undefined
      ? highlightSentenceId
      : autoHighlightId;

  return (
    <div className={styles.playerContainer}>
      <div className={styles.transcriptSection}>
        {!hasContent ? (
          <div className={styles.status}>暂无句子内容</div>
        ) : (
          <div
            className={styles.sentenceContainer}
            ref={transcriptContainerRef}
            style={{
              fontSize: typeof fontSize === 'number' ? `${fontSize}px` : undefined,
              lineHeight: typeof lineHeight === 'number' ? lineHeight : undefined,
            }}
          >
            <SentencesSection
              chapter={chapter}
              displayMode={displayMode}
              chineseTextColor={chineseTextColor}
              englishTextColor={englishTextColor}
              fontSize={fontSize}
              lineHeight={lineHeight}
              sentenceFavorites={sentenceFavorites}
              sentenceFavoriteLoading={sentenceFavoriteLoading}
              sentenceNoteLoading={sentenceNoteLoading}
              highlightSentenceId={effectiveHighlightId}
              backgroundColor={backgroundColor}
              chapterWordSet={chapterWordSet}
              sentenceTimings={sentenceTimings}
              onSentenceFavorite={onSentenceFavorite}
              onSentenceNote={onSentenceNote}
              onWordClick={onWordClick}
              onSentenceClick={handleSentenceClick}
              onSentencePronounce={handleSentencePronounce}
              onBlankAreaClick={isPlaying ? handlePause : handlePlay}
            />
          </div>
        )}
      </div>

      <div className={styles.audioWrapper}>
        {/* 隐藏的音频元素 */}
        <audio
          ref={audioEnRef}
          src={audioEnSrc}
          preload="auto"
          crossOrigin="anonymous"
          className={styles.hiddenAudio}
        />
        <audio
          ref={audioCnRef}
          src={audioCnSrc}
          preload="auto"
          crossOrigin="anonymous"
          className={styles.hiddenAudio}
        />
        <audio
          ref={audioDualRef}
          src={audioDualSrc}
          preload="auto"
          crossOrigin="anonymous"
          className={styles.hiddenAudio}
        />

        {!controlsHost && controlsWrapper}
      </div>

      {controlsHost && createPortal(controlsWrapper, controlsHost)}
    </div>
  );
};

export default ChapterPlayer;
